import { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

interface ConversationState {
  stage: string;
  data: {
    firstName?: string;
    lastName?: string;
    service?: string;
    sessionType?: string;
    platform?: string;
    date?: string;
    time?: string;
    email?: string;
    phone?: string;
  };
  unansweredCount: number;
  lastTopic?: string;
}

// Gelişmiş bilgi tabanı - Kısa ve öz yanıtlar
const knowledgeBase = {
  services: {
    astroloji: {
      keywords: [
        'astroloji', 'astrolog', 'doğum haritası', 'harita', 'burç', 'yıldız', 
        'gezegen', 'sinastri', 'transit', 'yükselen', 'öngörü', 'kariyer astroloji'
      ],
      responses: [
        `🌟 **Astrolojik Danışmanlık Hizmetlerim:**

• Doğum Haritası Analizi (90 dk)
• İlişki Uyumu / Sinastri (75 dk)
• Yıllık Öngörü (60 dk)
• Kariyer Danışmanlığı (60 dk)

Hangi hizmet ilginizi çekti?`,
        `✨ **Astroloji Seansları:**

Doğum haritanız, ruhunuzun parmak izidir.

• Kişisel Harita Okuma
• İlişki Analizi
• Gelecek Öngörüsü
• Kariyer Rehberliği

Daha fazla bilgi ister misiniz?`
      ],
      followUp: [
        'Hangi konuda analiz istersiniz?',
        'Randevu almak ister misiniz?'
      ]
    },
    spirituel: {
      keywords: [
        'spirituel', 'spiritüel', 'ruhsal', 'ruh', 'manevi', 'meditasyon', 
        'farkındalık', 'içsel', 'bilinç', 'yaşam amacı', 'dönüşüm'
      ],
      responses: [
        `✨ **Spiritüel Danışmanlık:**

• Ruhsal Rehberlik (60 dk)
• Yaşam Amacı Keşfi (75 dk)
• İçsel Dönüşüm (60 dk)
• Rüya Yorumlama (45 dk)

Size nasıl yardımcı olabilirim?`,
        `🌙 **Ruhsal Gelişim Hizmetleri:**

İçsel yolculuğunuzda yanınızdayım.

• Meditasyon & Farkındalık
• Yaşam Amacı
• İçsel Şifa
• Rüya Analizi

Hangi konuda destek istersiniz?`
      ],
      followUp: [
        'Hangi konuda rehberlik istersiniz?',
        'Randevu almak ister misiniz?'
      ]
    },
    enerji: {
      keywords: [
        'enerji', 'şifa', 'healing', 'reiki', 'çakra', 'aura', 'kristal', 
        'frekans', 'titreşim', 'uzaktan şifa', 'enerji temizliği'
      ],
      responses: [
        `💫 **Şifa & Enerji Çalışmaları:**

• Enerji Şifa Seansı (60 dk)
• Çakra Dengeleme (75 dk)
• Frekans Çalışması (60 dk)
• Uzaktan Şifa (45 dk)

Hangi şifa yöntemine ihtiyacınız var?`,
        `🔮 **Enerji Şifası:**

Enerji alanınızı temizleyin ve dengeleyin.

• Reiki Şifası
• Kristal Terapi
• Ses Şifası
• Çakra Çalışması

Size nasıl yardımcı olabilirim?`
      ],
      followUp: [
        'Hangi konuda şifa istersiniz?',
        'Randevu almak ister misiniz?'
      ]
    }
  },
  egitimler: {
    keywords: [
      'eğitim', 'kurs', 'öğren', 'ders', 'program', 'sertifika', 'atölye', 
      'workshop', 'seminer', 'online eğitim'
    ],
    responses: [
      `📚 **Eğitim Programları:**

• Temel Astroloji (12 hafta - 8.500 TL)
• İleri Astroloji (16 hafta - 12.000 TL)
• Enerji Çalışmaları (10 hafta - 7.500 TL)
• Spiritüel Gelişim (8 hafta - 6.000 TL)
• Numeroloji (6 hafta - 5.500 TL)

Hangi eğitim ilginizi çekti?`
    ],
    followUp: [
      'Detaylı bilgi ister misiniz?',
      'Kayıt olmak ister misiniz?'
    ]
  },
  hakkimda: {
    keywords: [
      'hakkında', 'kimsin', 'deniz erin', 'tanı', 'deneyim', 'sertifika'
    ],
    responses: [
      `👋 **Ben Deniz Erin**

🌟 15+ yıl Astrolog Danışman
✨ 12+ yıl Spiritüel Danışman
💫 10+ yıl Şifa Uzmanı

Binlerce kişiye rehberlik ettim.

Size nasıl yardımcı olabilirim?`
    ],
    followUp: [
      'Hangi hizmet ilginizi çekti?',
      'Randevu almak ister misiniz?'
    ]
  },
  seans: {
    keywords: [
      'seans', 'nasıl', 'süre', 'online', 'zoom', 'görüşme', 'yüz yüze'
    ],
    responses: [
      `📋 **Seans Süreci:**

1️⃣ Randevu & Bilgi Toplama
2️⃣ Hazırlık Aşaması
3️⃣ Seans (Online/Yüz yüze)
4️⃣ Seans Sonrası Destek

⏱️ Süreler: 45-90 dakika
💻 Format: Zoom, Meet, WhatsApp
📍 Yüz yüze: Ankara

Randevu almak ister misiniz?`
    ],
    followUp: [
      'Online mi yüz yüze mi tercih edersiniz?',
      'Hangi hizmet için randevu istersiniz?'
    ]
  },
  ucret: {
    keywords: [
      'ücret', 'fiyat', 'para', 'ne kadar', 'kaç lira', 'ödeme', 'taksit'
    ],
    responses: [
      `💰 **Ücretlendirme:**

Hizmet ücretleri değişkenlik gösterir.

📱 WhatsApp: +90 536 646 03 17
📧 E-posta: info@denizerin.com

**Eğitim Ücretleri:**
• Temel Astroloji: 8.500 TL
• İleri Astroloji: 12.000 TL
• Enerji: 7.500 TL
• Spiritüel: 6.000 TL

💳 Ödeme: Havale, Kredi Kartı, Nakit

Detaylı bilgi ister misiniz?`
    ],
    followUp: [
      'Hangi hizmet için fiyat öğrenmek istersiniz?',
      'Taksit seçeneği ilginizi çeker mi?'
    ]
  },
  iletisim: {
    keywords: [
      'iletişim', 'telefon', 'mail', 'adres', 'whatsapp', 'ulaş', 'ara'
    ],
    responses: [
      `📞 **İletişim:**

📱 WhatsApp: +90 536 646 03 17
📧 E-posta: info@denizerin.com

🕐 Çalışma Saatleri:
• Pzt-Cuma: 09:00-20:00
• Cumartesi: 10:00-18:00

📍 Yüz yüze: Ankara

Hemen iletişime geçmek ister misiniz?`
    ],
    followUp: [
      'WhatsApp mı e-posta mı tercih edersiniz?',
      'Randevu almak ister misiniz?'
    ]
  },
  randevu: {
    keywords: [
      'randevu', 'seans al', 'görüşme', 'tarih', 'saat', 'müsait'
    ],
    responses: [
      `📅 **Randevu Almak İster misiniz?**

Size yardımcı olabilirim!

Hangi hizmet için randevu almak istersiniz?

1️⃣ Astrolojik Danışmanlık
2️⃣ Spirituel Danışmanlık
3️⃣ Şifa & Enerji

Seçiminizi belirtin.`
    ],
    followUp: []
  }
};

// Gelişmiş yanıt bulma
const findBestMatch = (userMessage: string): { response: string; topic: string; confidence: number } | null => {
  const lowerMessage = userMessage.toLowerCase();
  let bestMatch: { response: string; topic: string; confidence: number } | null = null;
  let highestScore = 0;

  const allCategories = {
    ...knowledgeBase.services,
    egitimler: knowledgeBase.egitimler,
    hakkimda: knowledgeBase.hakkimda,
    seans: knowledgeBase.seans,
    ucret: knowledgeBase.ucret,
    iletisim: knowledgeBase.iletisim,
    randevu: knowledgeBase.randevu
  };

  for (const [key, category] of Object.entries(allCategories)) {
    if (!category.keywords) continue;

    let matchScore = 0;
    let matchedKeywords = 0;

    for (const keyword of category.keywords) {
      if (lowerMessage.includes(keyword)) {
        matchedKeywords++;
        matchScore += keyword.length;
      }
    }

    if (matchedKeywords > 0 && matchScore > highestScore) {
      highestScore = matchScore;
      
      let responseText = '';
      if ('responses' in category && Array.isArray(category.responses)) {
        const randomIndex = Math.floor(Math.random() * category.responses.length);
        responseText = category.responses[randomIndex];
      }

      bestMatch = {
        response: responseText,
        topic: key,
        confidence: matchedKeywords
      };
    }
  }

  return bestMatch;
};

// Takip sorusu
const getFollowUpQuestion = (topic: string): string => {
  const allCategories = {
    ...knowledgeBase.services,
    egitimler: knowledgeBase.egitimler,
    hakkimda: knowledgeBase.hakkimda,
    seans: knowledgeBase.seans,
    ucret: knowledgeBase.ucret,
    iletisim: knowledgeBase.iletisim,
    randevu: knowledgeBase.randevu
  };

  const category = allCategories[topic as keyof typeof allCategories];
  if (category && 'followUp' in category && Array.isArray(category.followUp) && category.followUp.length > 0) {
    const randomIndex = Math.floor(Math.random() * category.followUp.length);
    return '\n\n' + category.followUp[randomIndex];
  }

  return '';
};

// İsim çıkarma
const extractName = (message: string): { firstName?: string; lastName?: string } => {
  const lowerMessage = message.toLowerCase();
  
  // "Adım X Soyadım Y" formatı
  const pattern1 = /(?:adım|ismim)\s+(\w+)(?:\s+(?:soyadım|soyismim))?\s+(\w+)?/i;
  const match1 = message.match(pattern1);
  if (match1) {
    return {
      firstName: match1[1],
      lastName: match1[2] || undefined
    };
  }

  // "Ben X Y" formatı
  const pattern2 = /ben\s+(\w+)\s+(\w+)/i;
  const match2 = message.match(pattern2);
  if (match2) {
    return {
      firstName: match2[1],
      lastName: match2[2]
    };
  }

  // Sadece "X Y" formatı (2 kelime)
  const words = message.trim().split(/\s+/);
  if (words.length === 2 && words[0].length > 1 && words[1].length > 1) {
    // Soru kelimesi içermiyorsa
    if (!lowerMessage.includes('nedir') && !lowerMessage.includes('nasıl') && 
        !lowerMessage.includes('ne') && !lowerMessage.includes('hangi')) {
      return {
        firstName: words[0],
        lastName: words[1]
      };
    }
  }

  return {};
};

const ChatPage = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Merhaba! Hoş geldiniz. 🌟\n\nSize daha iyi yardımcı olabilmek için isim ve soyisminizi öğrenebilir miyim?',
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isMobileChatOpen, setIsMobileChatOpen] = useState(false);
  const [conversationState, setConversationState] = useState<ConversationState>({
    stage: 'asking_name',
    data: {},
    unansweredCount: 0
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      scrollToBottom();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [messages, isTyping]);

  const isMobile = () => {
    return window.innerWidth < 1024;
  };

  useEffect(() => {
    if (isMobileChatOpen && isMobile()) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileChatOpen]);

  const submitAppointment = async (appointmentData: any) => {
    try {
      const formData = new URLSearchParams();
      formData.append('ad_soyad', `${appointmentData.firstName || ''} ${appointmentData.lastName || ''}`.trim());
      formData.append('hizmet', appointmentData.service || '');
      formData.append('seans_tipi', appointmentData.sessionType || '');
      formData.append('platform', appointmentData.platform || '');
      formData.append('tarih', appointmentData.date || '');
      formData.append('saat', appointmentData.time || '');
      formData.append('email', appointmentData.email || '');
      formData.append('telefon', appointmentData.phone || '');

      const response = await fetch('https://readdy.ai/api/form/d56ibvm9uelhi99933v0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        return { success: true };
      } else {
        return { success: false, error: 'Form gönderilemedi' };
      }
    } catch (error) {
      console.error('Randevu gönderme hatası:', error);
      return { success: false, error: 'Bağlantı hatası' };
    }
  };

  const handleBotResponse = async (userMessage: string) => {
    setIsTyping(true);

    setTimeout(async () => {
      let botResponse = '';
      const newState = { ...conversationState };
      const lowerMessage = userMessage.toLowerCase();

      // İsim toplama aşaması
      if (conversationState.stage === 'asking_name') {
        const nameData = extractName(userMessage);
        
        if (nameData.firstName) {
          newState.data.firstName = nameData.firstName;
          newState.data.lastName = nameData.lastName;
          
          const fullName = nameData.lastName 
            ? `${nameData.firstName} ${nameData.lastName}` 
            : nameData.firstName;
          
          botResponse = `Hoş geldiniz ${fullName}! 🌟\n\nSize nasıl yardımcı olabilirim?\n\n• Hizmetler hakkında bilgi\n• Randevu alma\n• Eğitim programları\n• İletişim`;
          newState.stage = 'conversation';
          newState.unansweredCount = 0;
        } else {
          botResponse = 'İsminizi anlayamadım. 😊\n\nLütfen "Adım Ahmet Yılmaz" şeklinde yazabilir misiniz?';
        }
      }
      // Randevu akışı
      else if (conversationState.stage === 'asking_service') {
        if (lowerMessage.includes('1') || lowerMessage.includes('astroloji')) {
          newState.data.service = 'Astrolojik Danışmanlık';
          botResponse = 'Seansınızı nasıl gerçekleştirmek istersiniz?\n\n🏠 Yüz yüze\n💻 Online\n\nHangisini tercih edersiniz?';
          newState.stage = 'asking_format';
        } else if (lowerMessage.includes('2') || lowerMessage.includes('spirituel') || lowerMessage.includes('spiritüel')) {
          newState.data.service = 'Spirituel Danışmanlık';
          botResponse = 'Seansınızı nasıl gerçekleştirmek istersiniz?\n\n🏠 Yüz yüze\n💻 Online\n\nHangisini tercih edersiniz?';
          newState.stage = 'asking_format';
        } else if (lowerMessage.includes('3') || lowerMessage.includes('şifa') || lowerMessage.includes('enerji')) {
          newState.data.service = 'Şifa & Enerji Çalışmaları';
          botResponse = 'Seansınızı nasıl gerçekleştirmek istersiniz?\n\n🏠 Yüz yüze\n💻 Online\n\nHangisini tercih edersiniz?';
          newState.stage = 'asking_format';
        } else {
          botResponse = 'Lütfen 1, 2 veya 3 seçin.';
        }
      }
      else if (conversationState.stage === 'asking_format') {
        if (lowerMessage.includes('online')) {
          newState.data.sessionType = 'Online';
          botResponse = 'Hangi platformu tercih edersiniz?\n\n📹 Zoom\n📹 Google Meet\n💬 WhatsApp\n\nSeçiminizi belirtin.';
          newState.stage = 'asking_platform';
        } else if (lowerMessage.includes('yüz yüze') || lowerMessage.includes('yüzyüze')) {
          newState.data.sessionType = 'Yüz yüze';
          botResponse = 'Harika! Bilgilerinizi paylaşın:\n\n📅 Tarih (örn: 15 Ocak)\n🕐 Saat (örn: 14:00)\n📧 E-posta\n📱 Telefon\n\nÖrnek: "20 Ocak, 15:00, ornek@email.com, 0555 555 5555"';
          newState.stage = 'asking_datetime';
        } else {
          botResponse = 'Lütfen "Online" veya "Yüz yüze" seçin.';
        }
      }
      else if (conversationState.stage === 'asking_platform') {
        if (lowerMessage.includes('zoom')) {
          newState.data.platform = 'Zoom';
        } else if (lowerMessage.includes('google') || lowerMessage.includes('meet')) {
          newState.data.platform = 'Google Meet';
        } else if (lowerMessage.includes('whatsapp')) {
          newState.data.platform = 'WhatsApp';
        }

        if (newState.data.platform) {
          botResponse = 'Bilgilerinizi paylaşın:\n\n📅 Tarih (örn: 15 Ocak)\n🕐 Saat (örn: 14:00)\n📧 E-posta\n📱 Telefon\n\nÖrnek: "20 Ocak, 15:00, ornek@email.com, 0555 555 5555"';
          newState.stage = 'asking_datetime';
        } else {
          botResponse = 'Lütfen Zoom, Google Meet veya WhatsApp seçin.';
        }
      }
      else if (conversationState.stage === 'asking_datetime') {
        const emailMatch = userMessage.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
        const phoneMatch = userMessage.match(/[\d\s()+-]{10,}/);
        const hasDate = /\d{1,2}\s*(ocak|şubat|mart|nisan|mayıs|haziran|temmuz|ağustos|eylül|ekim|kasım|aralık|\/|-|\.)/.test(lowerMessage);

        if (emailMatch && phoneMatch && hasDate) {
          newState.data.email = emailMatch[0];
          newState.data.phone = phoneMatch[0].trim();
          newState.data.date = userMessage.split(',')[0]?.trim() || '';
          newState.data.time = userMessage.split(',')[1]?.trim() || '';

          const result = await submitAppointment(newState.data);

          if (result.success) {
            const fullName = newState.data.lastName 
              ? `${newState.data.firstName} ${newState.data.lastName}` 
              : newState.data.firstName;
            
            botResponse = `✅ Randevunuz kaydedildi ${fullName}!\n\n📋 Özet:\n🔹 ${newState.data.service}\n🔹 ${newState.data.sessionType}\n${newState.data.platform ? `🔹 ${newState.data.platform}\n` : ''}🔹 ${newState.data.date}\n🔹 ${newState.data.time}\n\n✨ En kısa sürede dönüş yapılacak!`;
            newState.stage = 'conversation';
            newState.unansweredCount = 0;
          } else {
            botResponse = `❌ Bir hata oluştu.\n\nLütfen WhatsApp'tan ulaşın:\n📱 +90 536 646 03 17`;
            newState.stage = 'conversation';
          }
        } else {
          botResponse = 'Lütfen tüm bilgileri eksiksiz paylaşın:\n\n📅 Tarih\n🕐 Saat\n📧 E-posta\n📱 Telefon';
        }
      }
      // Normal konuşma
      else if (conversationState.stage === 'conversation') {
        // Selamlama
        const greetings = ['merhaba', 'selam', 'günaydın', 'iyi günler', 'hey'];
        const isGreeting = greetings.some(g => lowerMessage.includes(g));

        if (isGreeting) {
          const fullName = newState.data.lastName 
            ? `${newState.data.firstName} ${newState.data.lastName}` 
            : newState.data.firstName;
          
          botResponse = `Merhaba ${fullName}! 🌟\n\nSize nasıl yardımcı olabilirim?`;
          newState.unansweredCount = 0;
        }
        // Randevu talebi
        else if (lowerMessage.includes('randevu') || lowerMessage.includes('seans al')) {
          botResponse = `Hangi hizmet için randevu almak istersiniz?\n\n1️⃣ Astrolojik Danışmanlık\n2️⃣ Spirituel Danışmanlık\n3️⃣ Şifa & Enerji`;
          newState.stage = 'asking_service';
          newState.unansweredCount = 0;
        }
        // Bilgi tabanından yanıt
        else {
          const matchResult = findBestMatch(userMessage);
          
          if (matchResult && matchResult.confidence > 0) {
            botResponse = matchResult.response;
            newState.lastTopic = matchResult.topic;
            newState.unansweredCount = 0;

            const followUp = getFollowUpQuestion(matchResult.topic);
            if (followUp) {
              botResponse += followUp;
            }
          } else {
            // Cevaplanamadı - WhatsApp'a yönlendir
            newState.unansweredCount = (conversationState.unansweredCount || 0) + 1;
            
            const fullName = newState.data.lastName 
              ? `${newState.data.firstName} ${newState.data.lastName}` 
              : newState.data.firstName;
            
            botResponse = `Üzgünüm ${fullName}, bu konuda size daha iyi yardımcı olabilmek için canlı desteğe aktarıyorum. 🙏\n\n📱 **WhatsApp ile İletişime Geçin:**\n\n👉 +90 536 646 03 17`;
            newState.unansweredCount = 0;
          }
        }
      }

      setIsTyping(false);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now().toString(),
          text: botResponse,
          sender: 'bot',
          timestamp: new Date(),
        },
      ]);
      setConversationState(newState);
    }, 1200);
  };

  const handleSendMessage = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    const messageText = inputValue.trim();
    if (!messageText) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: messageText,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    
    handleBotResponse(messageText);
  };

  const handleMobileChatClick = () => {
    if (isMobile()) {
      setIsMobileChatOpen(true);
    }
  };

  const handleCloseMobileChat = () => {
    setIsMobileChatOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      <Navbar />
      
      <div className="pt-24 pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header - Masaüstü */}
          <div className="text-center mb-8 hidden lg:block">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Sohbet & Randevu
            </h1>
            <p className="text-lg text-gray-600">
              Sorularınızı sorun veya randevu alın
            </p>
          </div>

          {/* Mobil başlat butonu */}
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Sohbet & Randevu
            </h1>
            <p className="text-base text-gray-600 mb-6">
              Sorularınızı sorun veya randevu alın
            </p>
            <button
              onClick={handleMobileChatClick}
              className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full text-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg cursor-pointer whitespace-nowrap"
            >
              <i className="ri-chat-3-line mr-2"></i>
              Sohbeti Başlat
            </button>
          </div>

          {/* Masaüstü Chat */}
          <div className="hidden lg:block bg-white rounded-2xl shadow-xl overflow-hidden" style={{ height: '600px' }}>
            <div 
              ref={messagesContainerRef}
              className="h-[calc(100%-80px)] overflow-y-auto p-6 space-y-4"
            >
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${
                    message.sender === 'user' ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-[70%] rounded-2xl px-4 py-3 ${
                      message.sender === 'user'
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-line">{message.text}</p>
                    {message.sender === 'bot' && message.text.includes('WhatsApp ile İletişime Geçin') && (
                      <a
                        href="https://wa.me/905366460317"
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="inline-block mt-3 px-6 py-2.5 bg-[#25D366] text-white rounded-full text-sm font-medium hover:bg-[#20BA5A] transition-all duration-300 cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-whatsapp-line mr-2"></i>
                        WhatsApp'tan Yaz
                      </a>
                    )}
                    <span className="text-xs opacity-70 mt-1 block">
                      {message.timestamp.toLocaleTimeString('tr-TR', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                </div>
              ))}
              
              {isTyping && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 rounded-2xl px-4 py-3">
                    <div className="flex space-x-2">
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            <form 
              onSubmit={handleSendMessage}
              className="border-t border-gray-200 p-4 bg-white"
              data-readdy-form
            >
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Mesajınızı yazın..."
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                />
                <button
                  type="submit"
                  disabled={!inputValue.trim() || isTyping}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap flex items-center justify-center w-12 h-12 cursor-pointer"
                >
                  <i className="ri-send-plane-fill text-lg"></i>
                </button>
              </div>
            </form>
          </div>

          {/* Mobil Tam Ekran Chat */}
          {isMobileChatOpen && (
            <div className="lg:hidden fixed inset-0 z-[9999] bg-white flex flex-col">
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-4 py-4 flex items-center justify-between shadow-lg">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <i className="ri-robot-2-line text-xl"></i>
                  </div>
                  <div>
                    <h2 className="font-semibold text-base">Deniz Erin Asistan</h2>
                    <p className="text-xs text-white/80">Online</p>
                  </div>
                </div>
                <button
                  onClick={handleCloseMobileChat}
                  className="w-10 h-10 flex items-center justify-center text-white hover:bg-white/20 rounded-full transition-all cursor-pointer"
                  aria-label="Kapat"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div 
                ref={messagesContainerRef}
                className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50"
              >
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${
                      message.sender === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div
                      className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        message.sender === 'user'
                          ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                          : 'bg-white text-gray-900 shadow-sm'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-line">{message.text}</p>
                      {message.sender === 'bot' && message.text.includes('WhatsApp ile İletişime Geçin') && (
                        <a
                          href="https://wa.me/905366460317"
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="inline-block mt-3 px-6 py-2.5 bg-[#25D366] text-white rounded-full text-sm font-medium hover:bg-[#20BA5A] transition-all duration-300 cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-whatsapp-line mr-2"></i>
                          WhatsApp'tan Yaz
                        </a>
                      )}
                      <span className="text-xs opacity-70 mt-1 block">
                        {message.timestamp.toLocaleTimeString('tr-TR', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </div>
                ))}
                
                {isTyping && (
                  <div className="flex justify-start">
                    <div className="bg-white rounded-2xl px-4 py-3 shadow-sm">
                      <div className="flex space-x-2">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>

              <form 
                onSubmit={handleSendMessage}
                className="border-t border-gray-200 p-4 bg-white"
                data-readdy-form
              >
                <div className="flex space-x-3">
                  <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="Mesajınızı yazın..."
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                  <button
                    type="submit"
                    disabled={!inputValue.trim() || isTyping}
                    className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all whitespace-nowrap flex items-center justify-center w-12 h-12 cursor-pointer"
                  >
                    <i className="ri-send-plane-fill text-lg"></i>
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ChatPage;
